import React, { useEffect, useRef } from 'react'

import useAttachmentUpload from '../../hooks/useAttachmentUpload'
import { useToast } from '../../context/ToastContext'
import AttachmentPreview from './AttachmentPreview'
import { checkAttachmentLimits, MAX_ATTACHMENTS, MAX_TOTAL_BYTES } from '../../utils/tickets/attachments'

const MAX_TOTAL_MB = Math.round(MAX_TOTAL_BYTES / (1024 * 1024))

// AttachmentUploader (design: Attachment_Uploader; Req 2.1, 2.2, 2.3, 2.4)
//
// Controlled attachment list editor. Uploads selected files and pasted images
// via `useAttachmentUpload`, merges the resulting attachment references into the
// caller-owned `attachments` list, renders each attachment through
// AttachmentPreview, and surfaces per-file upload errors (with a retry action)
// through the ToastContext.
const AttachmentUploader = ({
  customerId,
  ticketId,
  draftId,
  attachments = [],
  onAttachmentsChange = () => {},
  onPaste,
  registerPasteHandler
}) => {
  const { upload, uploading, error, retry } = useAttachmentUpload({ customerId })
  const { showToast } = useToast()

  // Avoid re-toasting the same failure on every render.
  const notifiedRef = useRef(new Set())

  // Surface upload failures as a toast naming the affected file(s) (Req 2.4).
  useEffect(() => {
    error.forEach(({ fileName }) => {
      if (!notifiedRef.current.has(fileName)) {
        notifiedRef.current.add(fileName)
        showToast(`Upload failed for ${fileName}`, 'red')
      }
    })

    // Clear the notified marker for files that are no longer failing so a
    // future failure can toast again.
    const failing = new Set(error.map((entry) => entry.fileName))
    notifiedRef.current.forEach((fileName) => {
      if (!failing.has(fileName)) notifiedRef.current.delete(fileName)
    })
  }, [error, showToast])

  // Upload the given files and merge the successful refs into the list.
  // Enforces the attachment guardrails (max count + cumulative size) before
  // uploading, rejecting any overflow files with a clear toast (both the file
  // picker and paste paths funnel through here).
  const uploadAndMerge = async (files) => {
    const fileList = Array.from(files || [])
    if (fileList.length === 0) return []

    const { accepted, rejected } = checkAttachmentLimits(attachments, fileList)

    if (rejected.length > 0) {
      const byCount = rejected.some((r) => r.reason === 'count')
      const bySize = rejected.some((r) => r.reason === 'size')
      if (byCount && bySize) {
        showToast(`Attachment limits reached (max ${MAX_ATTACHMENTS} files, ${MAX_TOTAL_MB} MB total). Some files were not added.`, 'red')
      } else if (byCount) {
        showToast(`You can attach at most ${MAX_ATTACHMENTS} files. Some files were not added.`, 'red')
      } else {
        showToast(`Attachments can total at most ${MAX_TOTAL_MB} MB. Some files were not added.`, 'red')
      }
    }

    if (accepted.length === 0) return []

    const newRefs = await upload(accepted, { ticketId, draftId })
    if (newRefs.length > 0) onAttachmentsChange([...attachments, ...newRefs])
    return newRefs
  }

  const handleFileChange = async (event) => {
    const { files } = event.target
    await uploadAndMerge(files)
    // Reset the input so selecting the same file again re-triggers change.
    event.target.value = ''
  }

  // Paste handler: capture image/* clipboard items, upload them, and merge the
  // resulting attachment references (Req 2.2). Also invoke a caller-provided
  // onPaste (e.g. the description textarea) so composition is preserved.
  const handlePaste = async (event) => {
    if (typeof onPaste === 'function') onPaste(event)

    const items = Array.from(event.clipboardData?.items || [])
    const imageFiles = items
      .filter((item) => item.type && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file) => file != null)

    if (imageFiles.length === 0) return

    await uploadAndMerge(imageFiles)
  }

  // Expose the paste handler to the parent so another element (e.g. the ticket
  // description textarea) can route paste events here. Re-register whenever the
  // current attachments change so the handler always merges into the latest list.
  useEffect(() => {
    if (typeof registerPasteHandler === 'function') {
      registerPasteHandler(() => handlePaste)
    }
  }, [attachments, registerPasteHandler])

  // Retry a previously-failed upload and merge on success (Req 2.4).
  const handleRetry = async (fileName) => {
    const result = await retry(fileName)
    if (result) onAttachmentsChange([...attachments, result])
  }

  const atLimit = attachments.length >= MAX_ATTACHMENTS

  return (
    <div className='my-2' onPaste={handlePaste}>
      <input
        type='file'
        multiple
        onChange={handleFileChange}
        disabled={uploading || atLimit}
        className='block w-full text-sm text-gray-700 disabled:opacity-50'
      />
      <p className='mt-1 text-xs text-gray-400'>
        Up to {MAX_ATTACHMENTS} files, {MAX_TOTAL_MB} MB total. {attachments.length}/{MAX_ATTACHMENTS} added.
      </p>

      {attachments.length > 0 && (
        <div className='mt-2 flex flex-wrap gap-2'>
          {attachments.map((attachment) => (
            <AttachmentPreview key={attachment.storagePath} attachment={attachment} />
          ))}
        </div>
      )}

      {error.length > 0 && (
        <ul className='mt-2 space-y-1'>
          {error.map(({ fileName }) => (
            <li key={fileName} className='flex items-center gap-2 text-sm text-red-600'>
              <span>Upload failed for {fileName}</span>
              <button
                type='button'
                onClick={() => handleRetry(fileName)}
                className='rounded border border-red-600 px-2 py-0.5 text-red-600 hover:bg-red-50'
              >
                Retry
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default AttachmentUploader
